import type { TileProps } from "../Tile";
import type { View } from "react-native";

import { useModalContext } from "../../../context/JMUIModalContextProvider";

import { useCallback, useEffect, useRef } from "react";

interface UseTileFocusRestorationProps {
  tiles: TileProps[];
  limit: number;
  focus: (view: View) => () => void;
  tileRefs: React.RefObject<(View | null)[]>;
}

export const useTileFocusRestoration = ({
  tiles,
  limit,
  focus,
  tileRefs,
}: UseTileFocusRestorationProps) => {
  const { setModalCleanupCallback } = useModalContext();
  const lastClickedTile = useRef<{ key: string; index: number } | null>(null);
  const latestData = useRef({ tiles, limit });

  // Keep latestData updated so the cleanup callback has fresh state
  useEffect(() => {
    latestData.current = { tiles, limit };
  }, [tiles, limit]);

  const getTargetIndex = (
    currentTiles: TileProps[],
    currentLimit: number,
    key: string,
    originalIndex: number,
  ) => {
    const newIndex = currentTiles.findIndex((t) => t.primaryText === key);

    if (newIndex !== -1) {
      // If the original tile is still visible, focus it
      if (newIndex < currentLimit) {
        return newIndex;
      }
      // If the original tile is now invisible, focus the tile at the old index.
      return originalIndex;
    }
    return -1;
  };

  const restoreFocus = useCallback(
    (targetIndex: number, key: string) => {
      setTimeout(() => {
        if (targetIndex !== -1) {
          const targetRef = tileRefs.current?.[targetIndex];
          if (targetRef) {
            focus(targetRef);
          }
        }
        // Clean up the ref after focus restoration attempts
        if (lastClickedTile.current?.key === key) {
          lastClickedTile.current = null;
        }
      }, 100);
    },
    [focus, tileRefs],
  );

  const handleTilePress = (tile: TileProps, index: number) => {
    if (!tile.onPress) return undefined;

    return () => {
      const key = tile.primaryText;
      lastClickedTile.current = { key, index };

      // Override the cleanup callback to handle focus restoration
      setModalCleanupCallback(() => {
        // Wait a tick for React to update props/refs if state changed on close
        setTimeout(() => {
          if (!lastClickedTile.current) return;

          const { tiles: currentTiles, limit: currentLimit } = latestData.current;
          const targetIndex = getTargetIndex(currentTiles, currentLimit, key, index);

          restoreFocus(targetIndex, key);
        }, 0);
      });

      tile.onPress?.();
    };
  };

  return { handleTilePress };
};
